import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Link, Box, Button } from "@mui/material";
import { LocationOn, Phone, Email } from "@mui/icons-material";
import { ConstantAPISContext } from "../../../context/ConstantAPISContext";
import { useNavigate } from "react-router";
import { ThemeContext } from "../../../context/themeProviderContext";
import { EBELEDIYE_LOGO, FOOTER_SAMPAS_TEXT, FOOTER_SAMPAS_LINK } from "../../../config/environment";

type RetryImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  maxRetries?: number;
  retryDelayMs?: number;
};

const RetryImage: React.FC<RetryImageProps> = ({
  src,
  maxRetries = 2,
  retryDelayMs = 800,
  ...imgProps
}) => {
  const [attempt, setAttempt] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setCurrentSrc(src);
    setAttempt(0);
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [src]);

  const handleError = () => {
    if (attempt >= maxRetries) {
      return;
    }
    const nextAttempt = attempt + 1;
    setAttempt(nextAttempt);

    timeoutRef.current = window.setTimeout(() => {
      const separator = src.includes("?") ? "&" : "?";
      setCurrentSrc(`${src}${separator}cb=${Date.now()}`);
    }, retryDelayMs);
  };

  return (
    <img
      loading="lazy"
      decoding="async"
      src={currentSrc}
      onError={handleError}
      {...imgProps}
    />
  );
};

const FooterOB = () => {
  const { menuListAltMenu } = useContext(ConstantAPISContext);
  const navigate = useNavigate();
  const { belediyeAdres, belediyeMail, belediyeTelefon, belediyeKurumAdi }: any =
    useContext(ThemeContext);

  return (
    <Box component="footer" className="m-3">
      <Box
        className="rounded-3"
        sx={{ bgcolor: "background.paper", py: 4 }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {menuListAltMenu.map((item, index) => {
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Button
                    onClick={() => {
                      if (item.ustBaslik.sayfaLink == null) {
                        // handle navigation when sayfaLink is null
                      } else if (item.ustBaslik.sayfaLink.startsWith("http")) {
                        window.location.href = item.ustBaslik.sayfaLink;
                      } else {
                        navigate(item.ustBaslik.sayfaLink);
                      }
                    }}
                    variant="text"
                    color="inherit"
                  >
                    <Typography variant="h6" color="textPrimary" gutterBottom>
                      {item.ustBaslik.menuAdi}
                    </Typography>
                  </Button>
                  <ul>
                    {item.altBasliklar.map((subItem: any, index: any) => (
                      <li key={index}>
                        <Button
                          onClick={() => {
                            if (subItem.sayfaLink == null) {
                              // handle navigation when sayfaLink is null
                            } else if (subItem.sayfaLink.startsWith("http")) {
                              window.location.href = subItem.sayfaLink;
                            } else {
                              navigate(subItem.sayfaLink);
                            }
                          }}
                          variant="text"
                        >
                          {subItem.menuAdi}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Grid>
              );
            })}

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" color="textPrimary" gutterBottom>
                İletişim Bilgileri
              </Typography>
              <Box>
                <RetryImage
                  src={(EBELEDIYE_LOGO as String).toString()}
                  alt="Belediyesi"
                  style={{ maxWidth: "100%", marginBottom: "1rem" }}
                />
                <Typography variant="body2" color="textPrimary">
                  <LocationOn /> {belediyeAdres}
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  <Phone /> {belediyeTelefon}
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  <Email /> {belediyeMail}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Copyright Section */}
      <Box
        sx={{
          bgcolor: 'white',
          color: '#333',
          textAlign: 'center',
          py: 2,
          mt: 0,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
          {belediyeKurumAdi} Copyright © {new Date().getFullYear()} Tüm Hakları Saklıdır
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          Powered By{' '}
          <Link
            href={FOOTER_SAMPAS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {FOOTER_SAMPAS_TEXT}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default FooterOB;
